import {
  CopyOutlined,
  EditOutlined,
  RightCircleOutlined
} from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Flex, Switch, TableColumnsType } from 'antd'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useQuestionnairesServiceActivateQuestionnaireEndpoint,
  useQuestionnairesServiceCopyQuestionnaireEndpoint,
  useQuestionnairesServiceDeactivateQuestionnaireEndpoint,
  useQuestionnairesServiceDeleteQuestionnaireEndpoint,
  useQuestionnairesServiceGetQuestionnaireListEndpointKey,
  useQuestionnairesServicePublishQuestionnaireEndpoint
} from 'src/api/queries'
import { GetQuestionnaireResponse, QuestionnaireState } from 'src/api/requests'
import { TableActionButtons, DeleteConfirm, Text } from 'src/components'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

import { QuestionnairesStatusTag } from '../components'

export const useConfig = () => {
  const { t, currentLang } = useLocales()

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { mutate: publish } =
    useQuestionnairesServicePublishQuestionnaireEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useQuestionnairesServiceGetQuestionnaireListEndpointKey]
        })
      }
    })

  const { mutate: deleteQuiz } =
    useQuestionnairesServiceDeleteQuestionnaireEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useQuestionnairesServiceGetQuestionnaireListEndpointKey]
        })
      }
    })

  const { mutate: makeCopy } =
    useQuestionnairesServiceCopyQuestionnaireEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useQuestionnairesServiceGetQuestionnaireListEndpointKey]
        })
      }
    })

  const { mutate: activate, isPending: activatePending } =
    useQuestionnairesServiceActivateQuestionnaireEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useQuestionnairesServiceGetQuestionnaireListEndpointKey]
        })
      }
    })

  const { mutate: deactivate, isPending: deactivatePending } =
    useQuestionnairesServiceDeactivateQuestionnaireEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useQuestionnairesServiceGetQuestionnaireListEndpointKey]
        })
      }
    })

  const onChangeStatus = ({
    id,
    state
  }: {
    id: string
    state: QuestionnaireState
  }) => {
    if (state === QuestionnaireState.ACTIVE) {
      deactivate({ id: String(id) })
    } else {
      activate({ id: String(id) })
    }
  }

  const publishActions = (data: GetQuestionnaireResponse) => [
    {
      key: 'edit',
      label: t('common.see_details'),
      icon: <EditOutlined />,
      onClick: () => navigate(paths.questionnaires.edit(String(data?.id)))
    },
    {
      key: 'copy',
      label: t('questionnaires.create_copy'),
      onClick: () =>
        makeCopy({
          id: String(data?.id),
          requestBody: {
            name: {
              ro: `${data?.name?.ro} - Copie`,
              ru: `${data?.name?.ru} - Копия`
            },
            description: {
              ro: data?.description?.ro,
              ru: data?.description?.ru
            }
          }
        }),
      icon: <CopyOutlined />
    }
  ]

  const getActionItems = useMemo(
    () => (data: GetQuestionnaireResponse) => {
      const isDraft = data?.state === QuestionnaireState.DRAFT

      return !isDraft
        ? publishActions(data)
        : [
            {
              key: 'publish',
              label: t('questionnaires.publish'),
              icon: <RightCircleOutlined />,
              onClick: () =>
                publish({
                  id: String(data?.id)
                })
            },
            {
              key: 'edit',
              label: t('common.edit'),
              icon: <EditOutlined />,
              onClick: () =>
                navigate(paths.questionnaires.edit(String(data?.id)))
            },
            {
              key: 'copy',
              label: t('questionnaires.create_copy'),
              onClick: () =>
                makeCopy({
                  id: String(data?.id),
                  requestBody: {
                    name: {
                      ro: `${data?.name?.ro} - Copie`,
                      ru: `${data?.name?.ru} - Копия`
                    },
                    description: {
                      ro: data?.description?.ro,
                      ru: data?.description?.ru
                    }
                  }
                }),
              icon: <CopyOutlined />
            },
            {
              key: 'delete',
              label: (
                <DeleteConfirm
                  title={t('common.delete')}
                  onConfirm={() => deleteQuiz({ id: String(data?.id) })}
                />
              ),
              danger: true
            }
          ]
    },
    [t]
  )

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'title',
        title: t('classifiers.title'),
        width: '60%',
        render: ({ name }) => <Text>{name?.[currentLang?.value]}</Text>
      },
      {
        key: 'questionsCount',
        title: t('questionnaires.questions_count'),
        dataIndex: 'questionsCount',
        width: '20%'
      },
      {
        key: 'status',
        title: t('form.status'),
        width: '10%',
        render: ({ id, state }) => {
          const isDraft = state === QuestionnaireState.DRAFT
          const isActive = state === QuestionnaireState.ACTIVE

          return (
            <Flex gap={16}>
              <QuestionnairesStatusTag status={state} />
              {!isDraft && (
                <Switch
                  loading={activatePending || deactivatePending}
                  checked={isActive}
                  onChange={() => onChangeStatus({ id, state })}
                />
              )}
            </Flex>
          )
        }
      },
      {
        key: 'actions',
        render: (data) => <TableActionButtons items={getActionItems(data)} />
      }
    ],
    [t]
  )

  return { columns }
}
